import { getProviderName } from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/__types__/uptime.config.query"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type StatusTargetOverride = NonNullable<
  UptimeConfigQuery["getStatusConfig"]["TargetOverrides"][number]
>
export type StatusProvider = NonNullable<
  UptimeConfigQuery["getStatusConfig"]["Providers"][number]
>

export type UptimeEditPublicFormState = {
  displayName: string
  description: string
  isPublicHidden: boolean
  providerId: string
}

// findOverride scans the cached status-config for the row keyed by
// (Source, Identifier). Lookup is O(n) over a tiny list (n is the
// total number of overridden probes, which is typically <50) so a
// linear scan beats maintaining a memoised map by (Source,
// Identifier) for this surface.
export function findOverride(
  config: UptimeConfigQuery["getStatusConfig"] | undefined,
  target: { Source: string; Identifier: string },
): StatusTargetOverride | null {
  if (!config) return null
  for (const row of config.TargetOverrides) {
    if (row.Source === target.Source && row.Identifier === target.Identifier) {
      return row
    }
  }
  return null
}

// makeFormState pre-populates the editor with whatever is currently
// stored on disk. When no override row exists yet (the common case
// for a fresh probe) every field starts blank so the operator only
// has to fill in the values they actually want to override.
export function makeFormState(
  override: StatusTargetOverride | null,
): UptimeEditPublicFormState {
  return {
    displayName: override?.DisplayName ?? "",
    description: override?.Description ?? "",
    isPublicHidden: override?.IsPublicHidden ?? false,
    providerId: override?.ProviderId ?? "",
  }
}

// hasFormChanges tells the save button whether anything is actually
// different from what's persisted, so we can disable it when the
// operator has no edits queued. Keeps the comparison field-by-field
// to avoid spurious re-saves caused by whitespace differences in
// either input.
export function hasFormChanges(
  form: UptimeEditPublicFormState,
  override: StatusTargetOverride | null,
): boolean {
  const baseline = makeFormState(override)
  return (
    form.displayName.trim() !== baseline.displayName.trim() ||
    form.description.trim() !== baseline.description.trim() ||
    form.isPublicHidden !== baseline.isPublicHidden ||
    form.providerId !== baseline.providerId
  )
}

// derivePublicPreview computes what the public status page would
// show for this target given the current form state. Mirrors the
// fallback chain inside v2-status-api/publicstatus/services.go so
// the operator's preview matches what customers will actually see.
export function derivePublicPreview(
  target: UptimeTargetFragment,
  form: UptimeEditPublicFormState,
  config: UptimeConfigQuery["getStatusConfig"] | undefined,
): {
  displayName: string
  description: string
  providerName: string
  isHidden: boolean
} {
  const trimmedName = form.displayName.trim()
  const trimmedDescription = form.description.trim()
  const provider =
    form.providerId === ""
      ? null
      : (config?.Providers.find((p) => p.Id === form.providerId) ?? null)
  const fallbackProviderName = getProviderName(target)
  return {
    displayName:
      trimmedName === "" ? (target.Name ?? target.Identifier) : trimmedName,
    description: trimmedDescription,
    providerName: provider ? provider.Name : fallbackProviderName,
    isHidden: form.isPublicHidden,
  }
}

// PROVIDER_OVERRIDE_NONE_VALUE is the sentinel value used by the
// provider <Select>'s "auto-detect" option. Radix Select disallows
// empty strings so we use a non-empty marker that the form layer
// translates back to `null` before sending to the mutation.
export const PROVIDER_OVERRIDE_NONE_VALUE = "__auto__"
